/**
 * Double-click action menu. Floats as a vertical stack of StoneButtons
 * next to the target card. While open, the host card's per-card
 * overlays (status badges, damage HL, equipment peek) are suppressed
 * via the hover-overlay clear in GameBoard._openCardActionMenu.
 *
 * Position model: GameBoard projects the card's screen-space bbox at
 * menu-open time and hands it over via `cardBounds`. The stack tries
 * the card's right side first, top-aligned to the card; if the menu
 * would clip the canvas edge we flip to the left side, then clamp
 * vertically so it always fits on-canvas.
 *
 * Buttons share the StoneButton vocabulary used by EffectPromptDrawer
 * so prompts and actions feel like the same surface.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { onCanvasScaleChange } from '../../utils/uiScale';
import { playUI, UI } from '../../utils/arena/uiSounds';
import StoneButton from './StoneButton';
import { useEscDismiss } from './useEscDismiss';
import { t } from '../../localization/t';

const Z = 1100;
const GAP_CQW = 0.55;
const BUTTON_HEIGHT_CQW = 2.4;
// Horizontal breathing room between the card and the menu, plus a
// minimum margin against the canvas edges so the menu never sits
// flush against the letterbox border.
const CARD_GAP_PX = 12;
const EDGE_PAD_PX = 8;

export default function CardActionMenu({ menu, viewScale, onClose }) {
  return (
    <AnimatePresence>
      {menu && (
        <ActionStack
          menu={menu}
          viewScale={viewScale}
          onClose={onClose}
        />
      )}
    </AnimatePresence>
  );
}

function ActionStack({ menu, viewScale, onClose }) {
  const { items: rootItems, cardBounds, combatModal, noBackdrop, fixedPlacement, sideContent } = menu;
  const scale = viewScale || 1;
  const stackRef = useRef(null);
  useEscDismiss(true, onClose);
  // When non-null, the menu has drilled into a sub-list (e.g. picking
  // which activated ability to spend a tap on). Outside-click still
  // closes the whole menu; the Back row goes one level up.
  const [subItems, setSubItems] = useState(null);
  const items = subItems || rootItems;
  // The first actual button (skipping render-only caption rows) gets the
  // prominent 'primary' style. So a caption above the confirm button
  // doesn't steal the highlight.
  const firstButtonIdx = items.findIndex((it) => !it.render);
  // Default placement: right of the card, top-aligned. The
  // useLayoutEffect below corrects this once the stack has rendered
  // and we know its size. Using viewport-local coords because the
  // stack itself is `position: fixed` inside the `.app-canvas`
  // containing block.
  const [placement, setPlacement] = useState(() => initialPlacement(cardBounds, scale));
  // Canvas-local size of the prompt stack. Anchors the side preview
  // (combat matchup) as a SIBLING to the stack's right. Keeping the
  // preview out of the measured element is what keeps the stack's own
  // centering exact: a child (even absolutely positioned) risks
  // leaking into the measurement and shifting the prompt off-center.
  const [menuSize, setMenuSize] = useState(null);

  // Placement and the measured stack size are snapshots of the canvas
  // at measurement time. A window resize (or UI-scale change) rescales
  // every cqw dimension while those px snapshots stay frozen, drifting
  // the menu off-center and sliding the side preview over the prompt.
  // Re-measure whenever the canvas changes size, from any cause.
  const [layoutTick, setLayoutTick] = useState(0);
  useEffect(() => {
    const bump = () => setLayoutTick((t) => t + 1);
    const unsub = onCanvasScaleChange(bump);
    const canvasEl = document.querySelector('.app-canvas');
    const ro = typeof ResizeObserver !== 'undefined' && canvasEl ? new ResizeObserver(bump) : null;
    if (ro) ro.observe(canvasEl);
    return () => { unsub(); ro?.disconnect(); };
  }, []);

  useLayoutEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    // `.app-canvas` is the letterboxed containing block for fixed
    // descendants. Clamp against it (not the viewport) so the menu
    // never crosses the black letterbox bars.
    const canvasEl = document.querySelector('.app-canvas');
    const canvasRect = canvasEl?.getBoundingClientRect() ?? {
      left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight,
    };
    const rect = el.getBoundingClientRect();
    const menuW = rect.width;
    const menuH = rect.height;
    setMenuSize({ w: menuW / scale, h: menuH / scale });
    // Fixed placement (combat defend / intercept) floats the panel at a
    // screen edge, clear of the board so the player can click + drag the
    // highlighted units underneath it.
    if (fixedPlacement) {
      setPlacement(computeFixedPlacement(fixedPlacement, canvasRect, menuW, menuH, scale));
      return;
    }
    if (!cardBounds) return;
    setPlacement(computePlacement(cardBounds, canvasRect, menuW, menuH, scale));
  }, [cardBounds, scale, items.length, subItems, fixedPlacement, layoutTick]);

  return (
    <>
      {/* Click-anywhere-outside backdrop. pointer-events:auto so the
          first click closes the menu without committing an action. In
          combat mode the backdrop instead BLOCKS board interaction (it
          swallows the click) and does NOT dismiss. The player must pick
          one of the combat buttons. `noBackdrop` (the defend prompt)
          omits it entirely so the player can drag a unit on the board. */}
      {!noBackdrop && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: Z - 1,
            background: 'transparent',
          }}
          onMouseDown={combatModal ? (e) => e.stopPropagation() : () => { playUI(UI.CANCEL); onClose(); }}
        />
      )}
      <motion.div
        ref={stackRef}
        initial={{ opacity: 0, x: placement.side === 'left' ? 8 : -8 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: placement.side === 'left' ? 4 : -4, transition: { duration: 0.08 } }}
        transition={{ type: 'spring', stiffness: 700, damping: 30 }}
        style={{
          position: 'fixed',
          left: placement.x,
          top: placement.y,
          zIndex: Z,
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: `${GAP_CQW}cqw`,
          pointerEvents: 'auto',
        }}
      >
        {subItems && (
          <StoneButton
            key="__back"
            variant="secondary"
            heightCqw={BUTTON_HEIGHT_CQW}
            onClick={() => setSubItems(null)}
          >
            {t('← Back')}
          </StoneButton>
        )}
        {items.map((item, idx) => (
          item.render
            ? <div key={item.id} style={{ width: '100%' }}>{item.render({ onClose })}</div>
            : (
              <StoneButton
                key={item.id}
                variant={idx === firstButtonIdx && !subItems ? 'primary' : 'secondary'}
                heightCqw={BUTTON_HEIGHT_CQW}
                disabled={item.disabled}
                onClick={() => {
                  if (item.subItems) {
                    setSubItems(item.subItems);
                  } else {
                    item.onClick();
                    // Combat prompts manage their own open/close lifecycle
                    // through the engine, so don't auto-dismiss. The next
                    // phase (e.g. assign) re-opens the menu in place.
                    if (!combatModal) onClose();
                  }
                }}
              >
                {item.label}
              </StoneButton>
            )
        ))}
      </motion.div>
      {/* Side preview (defend/intercept matchup). A SIBLING of the
          measured stack, placed off the stack's right edge from its
          measured size, top-aligned with it so taller previews grow
          DOWN into the board (never up off-screen). The cqw gap keeps
          the offset proportional at every canvas scale. */}
      {sideContent && menuSize ? (
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, transition: { duration: 0.08 } }}
          transition={{ type: 'spring', stiffness: 700, damping: 30 }}
          style={{
            position: 'fixed',
            left: `calc(${placement.x + menuSize.w}px + 1.8cqw)`,
            top: placement.y,
            zIndex: Z,
            pointerEvents: 'none',
          }}
        >
          {sideContent()}
        </motion.div>
      ) : null}
    </>
  );
}

// The stack renders `position: fixed` inside `.app-canvas`, whose
// translateZ(0) makes it the containing block. So CSS left/top are
// measured from the CANVAS origin, not the viewport. All placement
// math runs in viewport px (that's what getBoundingClientRect and the
// projected card bbox speak), then converts at the end by subtracting
// the canvas origin. Skipping that conversion shifts the menu by the
// letterbox offset whenever the window isn't exactly 16:9.
function toCanvasLocal(x, y, canvasRect, scale, side) {
  return {
    x: (x - (canvasRect.left || 0)) / scale,
    y: (y - (canvasRect.top || 0)) / scale,
    side,
  };
}

// Float the panel at a fixed screen edge (combat defend / intercept),
// horizontally centered on the canvas and clear of the board cells so the
// player can click + drag the highlighted units underneath it. Top edge by
// default; 'bottom' pins it just above the hand region.
function computeFixedPlacement(where, canvasRect, menuW, menuH, scale) {
  const PAD = 16;
  const centerX = (canvasRect.left + canvasRect.right) / 2 - menuW / 2;
  const y = where === 'bottom'
    ? canvasRect.bottom - menuH - PAD
    : canvasRect.top + PAD;
  return toCanvasLocal(centerX, y, canvasRect, scale, 'right');
}

// Best-effort initial position before we've measured the stack.
// places it to the right of the card, top-aligned. The useLayoutEffect
// will refine this on the next frame once real dimensions are known.
function initialPlacement(cardBounds, scale) {
  if (!cardBounds) return { x: 0, y: 0, side: 'right' };
  const canvasRect = document.querySelector('.app-canvas')?.getBoundingClientRect()
    || { left: 0, top: 0 };
  return toCanvasLocal(
    cardBounds.cardRight + CARD_GAP_PX,
    cardBounds.cardTop,
    canvasRect,
    scale,
    'right',
  );
}

// Pick a side (right preferred, flip to left when the right side
// can't fit the menu) and clamp the vertical position so the whole
// menu fits between the canvas top and bottom.
function computePlacement(cardBounds, canvasRect, menuW, menuH, scale) {
  const rightX = cardBounds.cardRight + CARD_GAP_PX;
  const leftX = cardBounds.cardLeft - CARD_GAP_PX - menuW;
  const canvasRight = canvasRect.right - EDGE_PAD_PX;
  const canvasLeft = canvasRect.left + EDGE_PAD_PX;
  const canvasTop = canvasRect.top + EDGE_PAD_PX;
  const canvasBottom = canvasRect.bottom - EDGE_PAD_PX;

  // Prefer the right side; if it can't fit the menu, use the left.
  // When neither side fully fits (very tight layouts), keep whichever
  // has more room and let the horizontal clamp below take over.
  let side = 'right';
  let x = rightX;
  if (rightX + menuW > canvasRight) {
    const leftFits = leftX >= canvasLeft;
    const rightOverflow = rightX + menuW - canvasRight;
    const leftOverflow = canvasLeft - leftX;
    if (leftFits || leftOverflow < rightOverflow) {
      side = 'left';
      x = leftX;
    }
  }
  x = Math.max(canvasLeft, Math.min(x, canvasRight - menuW));

  // Vertical: align the menu's top to the card's on-screen top edge,
  // then clamp into the canvas.
  let y = cardBounds.cardTop;
  if (y + menuH > canvasBottom) y = canvasBottom - menuH;
  if (y < canvasTop) y = canvasTop;

  return toCanvasLocal(x, y, canvasRect, scale, side);
}
